import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { getYearGroupLabel } from "../../Constant/ConstantData";
import { formatWeekCommencingDate } from "../../Util/HelperFunction";

const MultiLineRegistrationChart = ({ registerData, userData }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      if (!Array.isArray(entries) || !entries.length) return;
      const entry = entries[0];
      setWidth(entry.contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
    };
  }, []);

  const colors = {
    year1: "#4318ff",
    year2: "#8553f4",
    year3: "#c900a4",
    year4: "#6c35db",
    year5: "#249a95",
    year6: "#f8ad4d",
    year7: "#fcd46b",
    year8: "#fb8acb",
    year9: "#be00ab",
    year10: "#160071",
    year11: "#c900a4",
    year12: "#6c35db",
    year13: "#249a95",
    year14: "#6c35db",
    year15: "#249a95",
    year16: "#f8ad4d",
    year18: "#f8ad4d",
    parent: "#249a95",
  };


  const yearKeys = registerData
    ? [
      ...new Set(
        registerData.flatMap((item) =>
          Object.keys(item).filter(
            (key) => key.startsWith("year") || key === "parent"
          )
        )
      ),
    ].sort((a, b) => {
      if (a === "parent") return 1;
      if (b === "parent") return -1;
      const numA = parseInt(a.replace("year", ""));
      const numB = parseInt(b.replace("year", ""));
      return numA - numB;
    })
    : [];


  const processedData = useMemo(() => {
    if (!registerData || registerData.length === 0) return [];


    const dataWithAllYears = registerData.map((item) => {
      const processedItem = { ...item };
      yearKeys.forEach((yearKey) => {
        if (!(yearKey in processedItem)) {
          processedItem[yearKey] = 0;
        }
      });
      return processedItem;
    });


    const cumulativeData = [];
    const runningTotals = {};


    yearKeys.forEach((yearKey) => {
      runningTotals[yearKey] = 0;
    });

    dataWithAllYears.forEach((item) => {
      const cumulativeItem = { ...item };

      yearKeys.forEach((yearKey) => {
        runningTotals[yearKey] += item[yearKey] || 0;
        cumulativeItem[yearKey] = runningTotals[yearKey];
      });

      cumulativeData.push(cumulativeItem);
    });

    return cumulativeData;
  }, [registerData, yearKeys]);

  const maxVal = useMemo(() => {
    if (!processedData || processedData.length === 0) return 15;
    let max = 0;
    processedData.forEach((item) => {
      yearKeys.forEach((key) => {
        const val = Number(item[key]);
        if (!isNaN(val) && val > max) {
          max = val;
        }
      });
    });
    return max;
  }, [processedData, yearKeys]);

  const yAxisTicks = useMemo(() => {
    let limit = Math.max(15, maxVal);
    
    let step = 3;
    if (limit > 2000) step = 500;
    else if (limit > 1000) step = 200;
    else if (limit > 500) step = 100;
    else if (limit > 200) step = 50;
    else if (limit > 100) step = 20;
    else if (limit > 50) step = 10;
    else if (limit > 20) step = 5;

    limit = Math.ceil(limit / step) * step;
    const ticks = [];
    for (let i = 0; i <= limit; i += step) {
      ticks.push(i);
    }
    return ticks;
  }, [maxVal]);

  const requirements = useMemo(() => {
    const N = processedData?.length || 0;
    if (N <= 1) {
      return { shouldScroll: false, chartWidth: "100%", interval: 0 };
    }
    const requiredWidth = (N - 1) * 75 + 80;
    const availableWidth = (width > 55 ? width - 55 : width) || 500;
    const shouldScroll = availableWidth < requiredWidth;
    
    return {
      shouldScroll,
      chartWidth: shouldScroll ? `${requiredWidth}px` : "100%",
      interval: 0
    };
  }, [processedData?.length, width]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          width: "100%",
          position: "relative",
        }}
      >
        {/* Frozen Left Y-Axis */}
        <div
          style={{
            width: "55px",
            flexShrink: 0,
            height: 340,
            zIndex: 2,
          }}
        >
          <ResponsiveContainer width="100%" height={340}>
            <LineChart
              data={processedData}
              margin={{
                top: 10,
                right: 0,
                left: 10,
                bottom: 20,
              }}
            >
              <YAxis
                allowDecimals={false}
                ticks={yAxisTicks}
                domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
                interval={0}
                tick={{ fontSize: 12 }}
                width={45}
              />
              <XAxis dataKey="date" hide height={85} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Scrollable Plot Area */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            overflowX: requirements.shouldScroll ? "auto" : "hidden",
            overflowY: "hidden",
            boxSizing: "border-box",
            scrollbarWidth: requirements.shouldScroll ? "auto" : "none",
            msOverflowStyle: requirements.shouldScroll ? "auto" : "none",
          }}
          className="chart-scroll-container"
        >
          <div
            style={{
              width: requirements.chartWidth,
              minWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <ResponsiveContainer height={340}>
              <LineChart
                data={processedData}
                margin={{
                  top: 10,
                  right: 30,
                  left: 10,
                  bottom: 20,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatWeekCommencingDate}
                  tick={{ fontSize: 11 }}
                  angle={-45}
                  textAnchor="end"
                  height={85}
                  dx={-4}
                  dy={4}
                  interval={requirements.interval}
                />
                <YAxis
                  hide
                  allowDecimals={false}
                  ticks={yAxisTicks}
                  domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
                  interval={0}
                />
                <Tooltip
                  labelFormatter={formatWeekCommencingDate}
                  content={({ payload, label }) => {
                    if (payload && payload.length > 0) {
                      const filteredPayload = payload.filter(
                        (item) => item.value !== 0
                      );
                      if (filteredPayload.length === 0) return null;

                      const total = filteredPayload.reduce(
                        (sum, item) => sum + item.value,
                        0
                      );

                      return (
                        <div
                          style={{
                            backgroundColor: "white",
                            padding: "10px",
                            border: "1px solid #ccc",
                          }}
                        >
                          <div style={{ fontWeight: "bold", marginBottom: "5px" }}>{formatWeekCommencingDate(label)}</div>
                          {filteredPayload.map((item, index) => (
                            <div key={index} style={{ color: item.color }}>
                              {item.name}: {item.value}
                            </div>
                          ))}
                          <div
                            style={{
                              borderTop: "1px solid #ccc",
                              marginTop: "4px",
                              paddingTop: "4px",
                            }}
                          >
                            Total: {total}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                
                {yearKeys.map((yearKey) => {
                  const displayName =
                    yearKey === "parent"
                      ? "Parents"
                      : getYearGroupLabel(yearKey.replace("year", "")) || `Year ${yearKey.replace("year", "")}`;
                  return (
                    <Line
                      key={yearKey}
                      type="monotone"
                      dataKey={yearKey}
                      stroke={colors[yearKey] || "#666"}
                      name={displayName}
                      dot={{ r: 3 }}
                      strokeWidth={2}
                      connectNulls={false}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Legend below the chart */}
      {yearKeys.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "15px",
            marginTop: "15px",
          }}
        >
          {yearKeys.map((yearKey) => {
            const isParent = yearKey === "parent";
            const rawYear = yearKey.replace("year", "");
            const name = isParent
              ? "Parents"
              : getYearGroupLabel(rawYear) || `Year ${rawYear}`;
            const color = colors[yearKey] || (isParent ? colors.parent : "#666");

            return (
              <div
                key={yearKey}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    backgroundColor: color,
                    borderRadius: "2px",
                  }}
                />
                <span style={{ fontSize: "12px", color: "#666" }}>
                  {name}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
  .row {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    width: 100%;
  }
  .col-xxl-7,
  .col-xxl-5 {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }
  .cmn-card-color.table-cell {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
  .details-chart {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }
  .chart-scroll-container {
    -webkit-overflow-scrolling: touch;
  }
  .chart-scroll-container::-webkit-scrollbar {
    height: 8px;
  }
  .chart-scroll-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  .chart-scroll-container::-webkit-scrollbar-thumb {
    background: #c3c3c3;
    border-radius: 4px;
  }
  .chart-scroll-container::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`}</style>
    </div>
  );
};

export default MultiLineRegistrationChart;
